// client/src/reservations/utils/availabilityLocal.js
import { msWindowsForDate } from './hours';
import { isPastCutoffHHMM } from './timeGate';

const toArr = (v) => Array.isArray(v) ? v : (v ? Object.values(v) : []);

// Generic hours resolver by "mode" used in rc_business_hour_rule_applies.mode
export function windowsForDate(hoursCtx, mode, itemId, dateISO) {
  if (!hoursCtx || !dateISO) return [];
  const rulesArr   = toArr(hoursCtx.rules);
  const appliesArr = toArr(hoursCtx.applies);

  const apply = appliesArr.find(a => {
    const m = String(a.mode || a.scope || '').toLowerCase();
    const id = a.item_id ?? a.ms_seating_config_id ?? a.seating_config_id ?? a.table_type_id ?? a.itemId;
    return m === mode && String(id) === String(itemId);
  });
  const fallbackRuleId = rulesArr.find(r => r.scope === 'all')?.id ?? rulesArr[0]?.id;
  const ruleId = apply?.hours_rule_id ?? fallbackRuleId;
  if (!ruleId) return [];

  const hoursRows = Array.isArray(hoursCtx.hours_by_rule)
    ? hoursCtx.hours_by_rule.filter(r => String(r.hours_rule_id) === String(ruleId))
    : (hoursCtx.hours_by_rule?.[ruleId] || []);
  const exRows = Array.isArray(hoursCtx.exceptions_by_rule)
    ? hoursCtx.exceptions_by_rule.filter(e => String(e.hours_rule_id) === String(ruleId))
    : (hoursCtx.exceptions_by_rule?.[ruleId] || []);

  const ex = exRows.find(e => String(e.exception_date).slice(0,10) === dateISO);
  if (ex) return ex.closed ? [] : normalizeWindows(ex.time_slots);

  const d = new Date(dateISO);
  const dow = ((d.getDay() + 6) % 7) + 1; // 1..7
  const row = hoursRows.find(r => Number(r.day_of_week) === dow);
  if (!row || row.closed) return [];
  return normalizeWindows(row.time_slots);
}

function normalizeWindows(arr) {
  return (arr || []).map(w => ({ start: w.start ?? w.open, end: w.end ?? w.close }))
                    .filter(w => w.start && w.end);
}
function hhmmAdd(startHHMM, minutes) {
  const h = +startHHMM.slice(0,2), m = +startHHMM.slice(3,5);
  let t = h*60 + m + Number(minutes||0);
  t = Math.max(0, Math.min(24*60, t));
  const hh = String(Math.floor(t/60)).padStart(2,'0');
  const mm = String(t%60).padStart(2,'0');
  return `${hh}:${mm}`;
}
function minutesBetweenHHMM(a, b) {
  return toMin(b) - toMin(a);
}
function windowsFromActive(serviceId, startHHMM, durationMin, activeMap) {
  const endHHMM = hhmmAdd(startHHMM, durationMin);
  const segs = activeMap.get(String(serviceId)) || null;
  if (!segs || !segs.length) return [{ start: startHHMM, end: endHHMM }]; // full-time
  const out = [];
  for (const seg of segs) {
    const s = hhmmAdd(startHHMM, seg.from_minute);
    const e = hhmmAdd(startHHMM, seg.to_minute);
    if (toMin(e) > toMin(s)) out.push({ start: s, end: e });
  }
  return out;
}
function equipWindowsForService(serviceId, startHHMM, durationMin, usageMap) {
  // returns Map(pool_id -> [{start,end},...])
  const rows = usageMap.get(String(serviceId)) || [];
  const endHHMM = hhmmAdd(startHHMM, durationMin);
  const map = new Map();
  for (const r of rows) {
    const w = (r.full_time || r.from_minute == null || r.to_minute == null)
      ? [{ start: startHHMM, end: endHHMM }]
      : [{ start: hhmmAdd(startHHMM, r.from_minute), end: hhmmAdd(startHHMM, r.to_minute) }];
    const list = map.get(r.pool_id) || [];
    list.push(...w);
    map.set(r.pool_id, list);
  }
  return map;
}
function anyOverlap(aList=[], bList=[]) {
  for (const a of aList) for (const b of bList) {
    if (toMin(a.start) < toMin(b.end) && toMin(a.end) > toMin(b.start)) return true;
  }
  return false;
}

function toMin(s){ return (+s.slice(0,2))*60 + (+s.slice(3,5)); }
function toHHMM(m){ const h=String(Math.floor(m/60)).padStart(2,'0'); const mm=String(m%60).padStart(2,'0'); return `${h}:${mm}`; }
export function subtractWindows(aList=[], bList=[]){
  const out=[];
  for (const a of (aList||[])) {
    let segs=[a];
    for (const b of (bList||[])) {
      const next=[];
      for (const s of segs) {
        const a1=toMin(s.start), a2=toMin(s.end);
        const b1=toMin(b.start), b2=toMin(b.end);
        if (a1==null||a2==null||a2<=a1) continue;
        if (b1==null||b2==null||b2<=b1 || b2<=a1 || b1>=a2) {
          next.push(s); // no overlap
        } else {
          // left piece
          if (b1>a1) next.push({ start: toHHMM(a1), end: toHHMM(Math.min(b1,a2)) });
          // right piece
          if (b2<a2) next.push({ start: toHHMM(Math.max(b2,a1)), end: toHHMM(a2) });
        }
      }
      segs=next;
      if (!segs.length) break;
    }
    out.push(...segs);
  }
  return out.filter(w => toMin(w.end) > toMin(w.start));
}
export function generateSlots(windows, durationMin, stepMin=15){
  const out=[];
  for (const w of (windows||[])) {
    const a=toMin(w.start), b=toMin(w.end);
    if (a==null || b==null || b<=a) continue;
    for (let t=a; t+durationMin<=b; t+=stepMin) {
      out.push({ start: toHHMM(t), end: toHHMM(t+durationMin) });
      if (out.length>=200) return out;
    }
  }
  return out;
}

export function intersectWindows(aList, bList){
  const out=[]; 
  for (const a of (aList||[])) {
    const a1=toMin(a.start), a2=toMin(a.end); if (a1==null || a2==null || a2<=a1) continue;
    for (const b of (bList||[])) {
      const b1=toMin(b.start), b2=toMin(b.end); if (b1==null || b2==null || b2<=b1) continue;
      const s=Math.max(a1,b1), e=Math.min(a2,b2);
      if (e>s) out.push({ start: toHHMM(s), end: toHHMM(e) });
    }
  }
  return out;
}

export function psSlotsLocal({
  dateISO,
  serviceId,
  durationMin,
  hoursCtx,
  psSchedule,          // [{service_id, employee_id, date, start_time, end_time}]
  employees,
  tz,
  firstBookableISO,
  live,
  empWork = [],        // [{employee_id, date, start_time, end_time}]
  empOff = [],         // [{employee_id, date}]
  employeeId = null,
  scheduleModeByService = {},
  party = 1,                 // NEW: how many simultaneous services we need to seat
  activePeriods = [],        // NEW: [{service_id, from_minute, to_minute}]
  equipPools = [],           // NEW: [{id, name, kind, quantity}]
  equipUsage = [] 
}) {
  if (!dateISO) return { available: [] };

  // normalize potentially object-shaped inputs to arrays
  const empWorkArr = toArr(empWork);
  const empOffArr = toArr(empOff);

  const dayBlocks = (live?.['ps_service'] || []).filter(b => b.slot_start?.slice(0,10) === dateISO);
  const isOff = (eid) => (empOffArr || []).some(x => String(x.employee_id) === String(eid) && x.date === dateISO);
  const activeByService = new Map();
  (activePeriods || []).forEach(r => {
    const k = String(r.service_id);
    const arr = activeByService.get(k) || [];
    arr.push({ from_minute: Number(r.from_minute || 0), to_minute: Number(r.to_minute || 0) });
    activeByService.set(k, arr);
  });
  const usageByService = new Map();
  (equipUsage || []).forEach(r => {
    const k = String(r.service_id);
    const arr = usageByService.get(k) || [];
    arr.push({
      pool_id: r.pool_id,
      full_time: !!r.full_time,
      from_minute: r.from_minute != null ? Number(r.from_minute) : null,
      to_minute:   r.to_minute   != null ? Number(r.to_minute)   : null
    });
    usageByService.set(k, arr);
  });
  const poolQty = new Map((equipPools||[]).map(p => [p.id, Number(p.quantity||0)]));
  // Managed windows indexed two ways:
  const managedByThisSvc = new Map();               // empId -> [{start,end}] for THIS service
  const managedUnionForService = [];
  const managedBySvcEmp  = new Map();               // `${serviceId}|${empId}` -> [{start,end}] for any service
  const managedAllByEmp  = new Map();               // empId -> union over ALL services

  (psSchedule || []).forEach(r => {
    if (r.date !== dateISO) return;
    // per-service key
    const key = `${r.service_id}|${r.employee_id}`;
    const arrSvcEmp = managedBySvcEmp.get(key) || [];
    arrSvcEmp.push({ start: r.start_time, end: r.end_time });
    managedBySvcEmp.set(key, arrSvcEmp);

    // aggregate per-emp
    const agg = managedAllByEmp.get(r.employee_id) || [];
    agg.push({ start: r.start_time, end: r.end_time });
    managedAllByEmp.set(r.employee_id, agg);

    // for this service specifically
    if (String(r.service_id) === String(serviceId)) {
      const arr = managedByThisSvc.get(r.employee_id) || [];
      arr.push({ start: r.start_time, end: r.end_time });
      managedByThisSvc.set(r.employee_id, arr);
      managedUnionForService.push({ start: r.start_time, end: r.end_time });
    }
  });
  // NEW: for equipment gating — check if a given HH:MM can accommodate `party` instances
  const equipOKCache = new Map(); // startHHMM -> boolean
  function hasEquipmentFor(startHHMM) {
    if (equipOKCache.has(startHHMM)) return equipOKCache.get(startHHMM);

    // Candidate's own equipment windows (per pool)
    const candEquip = equipWindowsForService(serviceId, startHHMM, durationMin, usageByService);
    if (candEquip.size === 0) { // no equipment required by this service
      equipOKCache.set(startHHMM, true); return true;
    }

    // Count overlaps from LIVE blocks for each pool at those windows
    for (const [poolId, candWins] of candEquip.entries()) {
      const qty = poolQty.get(poolId) ?? Infinity;
      if (!Number.isFinite(qty)) continue;

      // How many concurrent jobs already use this pool overlapping our windows?
      let concurrent = 0;
      for (const b of dayBlocks) {
        if (b.offering_id == null) continue;
        // existing booking windows for this pool (if that service even uses it)
        const bStart = b.slot_start.slice(11,16);
        const bDur   = Math.max(0, minutesBetweenHHMM(bStart, b.slot_end.slice(11,16)));
        if (bDur <= 0) continue;
        const bwinsByPool = equipWindowsForService(b.offering_id, bStart, bDur, usageByService);
        const bwins = bwinsByPool.get(poolId) || [];
        if (bwins.length && anyOverlap(candWins, bwins)) {
          const weight = Math.max(1, Number(b.party_size ?? b.guests_json?.party_size ?? 1));
          concurrent += weight;
        }
      }

      // We need `party` more units for this time
      if (concurrent + Math.max(1, Number(party||1)) > qty) {
        equipOKCache.set(startHHMM, false); return false;
      }
    }

    equipOKCache.set(startHHMM, true);
    return true;
  }

  // Work windows per-employee (same as before)
  const workByEmp = new Map();
  (empWorkArr || []).forEach(r => {
    if (r.date !== dateISO) return;
    const arr = workByEmp.get(r.employee_id) || [];
    arr.push({ start: r.start_time, end: r.end_time });
    workByEmp.set(r.employee_id, arr);
  });

  const pool = employeeId
    ? (employees || []).filter(e => String(e.id) === String(employeeId))
    : (employees || []);

  const groups = [];
  const mode = String(scheduleModeByService?.[serviceId] || 'business').toLowerCase();

  for (const emp of pool) {
    if (isOff(emp.id)) continue;

    const work = workByEmp.get(emp.id) || [];
    let rawEmpWindows = [];

    if (mode === 'employee') {
      // REQUIRE managed rows for THIS employee on THIS service,
      // then: availability = managed_this_service_for_emp − employee_work
      const managedThis = managedByThisSvc.get(emp.id) || [];
      if (!managedThis.length) continue;
      rawEmpWindows = subtractWindows(managedThis, work);
    } else {
      // BUSINESS mode:
      // usable = work − union(managed for this employee on this day by OTHER services)
      const otherManaged = [];
      for (const [k, arr] of managedBySvcEmp.entries()) {
        const [svcId, eid] = k.split('|');
        if (String(eid) !== String(emp.id)) continue;
        if (String(svcId) === String(serviceId)) continue; // exclude this service's own managed windows
        otherManaged.push(...arr);
      }
      rawEmpWindows = subtractWindows(work, otherManaged);
    }

    if (!rawEmpWindows.length) continue;

    const starts = generateSlots(rawEmpWindows, durationMin).filter(s => {
      if (isPastCutoffHHMM({ dayISO: dateISO, hhmm: s.start, firstBookableISO, tz })) return false;

      // 1) Employee overlap — only during ACTIVE minutes of services
      const candEmpActive = windowsFromActive(serviceId, s.start, durationMin, activeByService);
      for (const b of dayBlocks) {
        if (b.employee_id !== emp.id) continue;
        const bStart = b.slot_start.slice(11, 16);
        const bDur = Math.max(0, minutesBetweenHHMM(bStart, b.slot_end.slice(11, 16)));
        if (bDur <= 0) continue;
        const bActive = windowsFromActive(b.offering_id, bStart, bDur, activeByService);
        if (anyOverlap(candEmpActive, bActive)) return false;
      }

      // 2) Equipment gating — enough units for all pools across their active windows
      if (!hasEquipmentFor(s.start)) return false;

      return true;
    });


    if (starts.length) groups.push({ employee: { id: emp.id, name: emp.name }, slots: starts });
  }

  return { available: groups };
}



// Count how many employees can take each HH:MM start across the groups from psSlotsLocal
export function psCountStarts(groups = []) {
  const map = new Map();
  (groups || []).forEach(g => {
    (g.slots || []).forEach(s => {
      map.set(s.start, (map.get(s.start) || 0) + 1);
    });
  });
  return map;
}

export function psHasKSimultaneous(groups = [], k = 1) {
  if (k <= 1) return (groups || []).some(g => (g.slots || []).length > 0);
  const m = psCountStarts(groups);
  for (const v of m.values()) if (v >= k) return true;
  return false;
}

export function psTimesMeetingParty(groups = [], k = 1) {
  if (k <= 1) {
    // union of all starts
    const set = new Set();
    (groups || []).forEach(g => (g.slots || []).forEach(s => set.add(s.start)));
    return Array.from(set).sort();
  }
  const m = psCountStarts(groups);
  return Array.from(m.entries())
    .filter(([, cnt]) => cnt >= k)
    .map(([t]) => t)
    .sort();
}

// Classes: compute remaining seats from patterns + live overlaps
export function cbSessionsLocal({ dateISO, classId, capacity, cbSessions, tz, firstBookableISO, live }) {
  const d = new Date(dateISO);
  const dow = ((d.getDay()+6)%7)+1;
  const patterns = (cbSessions||[]).filter(s => s.class_id === classId && Number(s.day_of_week) === dow);
  if (!patterns.length) return { sessions: [] };

  const blocks = live?.['cb_class'] || [];

  const sessions = [];
  for (const s of patterns) {
    if (isPastCutoffHHMM({ dayISO: dateISO, hhmm: s.start_time, firstBookableISO, tz })) continue;
    const used = blocks.reduce((sum, x) => {
      if (String(x.offering_id) !== String(classId)) return sum;
      // Robust match: same date and same start HH:MM based on slot_start (no TZ conversion)
      const xDate = (x.slot_start || '').slice(0, 10);
      const xHHMM = (x.slot_start || '').slice(11, 16);
      if (!(xDate === dateISO && xHHMM === s.start_time)) return sum;
      const seats = Number(x.party_size ?? x.guests_json?.party_size ?? 1);
      return sum + (Number.isFinite(seats) ? seats : 1);
    }, 0);
    const remaining = Math.max(0, Number(capacity||0) - used);
    if (remaining > 0) sessions.push({
      session_id: s.session_id,
      start_time: s.start_time,
      remaining,
      instructor_employee_id: s.instructor_employee_id || null
    });
  }
  return { sessions };
}

// Accommodation: verify priced nights and remaining quantity from live overlaps
export function acAvailabilityLocal({ roomTypeId, startDate, endDate, quantity, seasons, live }) {
  console.log('acAvailabilityLocal', { roomTypeId, startDate, endDate, quantity, seasons, live });

  if (!startDate || !endDate) return { available: 0, reason: 'missing_dates' };
  const nights = acNightsBetween(startDate, endDate); // [start, end)
  if (!nights.length) return { available: 0, reason: 'zero_nights' };
  // every night must be priced
  const unpriced = nights.filter(d => !acFindSeasonPrice(roomTypeId, d, seasons).hasPrice);
  if (unpriced.length) return { available: 0, reason: 'no_price', unpriced };
  // inventory per night (reservations + holds)
  const remainingByNight = acRemainingByNight(roomTypeId, nights, quantity, live);
  const minRemaining = Math.min(...nights.map(d => remainingByNight[d] ?? 0));
  return { available: Math.max(0, minRemaining), nights, remainingByNight };
}
 

export function acNightsBetween(startISO, endISO) {
  const out = [];
  if (!startISO || !endISO) return out;
  const start = new Date(startISO);
  const end   = new Date(endISO);
  for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
    out.push(d.toISOString().slice(0,10));
  }
  return out;
}

export function acFindSeasonPrice(roomTypeId, dateISO, seasons=[]) {
  const row = (seasons || []).find(s =>
    String(s.room_type_id) === String(roomTypeId) &&
    s.start_date <= dateISO && dateISO <= s.end_date
  );
  const hasPrice = !!row && row.price_amount != null;
  const price = hasPrice ? Number(row.price_amount) : null;
  return { hasPrice, price };
}

export function acRemainingByNight(roomTypeId, nights, quantity, live) {
  // live is expected to be grouped by kind: { ac_room_type: Block[] } per useBusinessSocket/groupLiveByKind
  // If a raw bootstrap-live shape is passed by mistake ({ reservations, holds }),
  // coerce it here defensively.
  let acBlocks = live?.ac_room_type;
  if (!Array.isArray(acBlocks)) {
    const all = [
      ...((live?.reservations || [])).map(x => ({ ...x })),
      ...((live?.holds || [])).map(x => ({ ...x })),
    ];
    acBlocks = all.filter(x => x.offering_kind === 'ac_room_type');
  }
  const blocks = (acBlocks || []).filter(x => String(x.offering_id) === String(roomTypeId));
  const out = {};
  nights.forEach(n => { out[n] = Number(quantity || 0); });
  for (const b of blocks) {
    const rng = parsePgRange(b.nights);
    for (const n of nights) {
      if (nightInRange(n, rng)) {
        const used = Number(
          // prefer explicit party_size when present in live rows; default to 1
          (b.party_size != null ? b.party_size : (b.guests_json?.party_size ?? 1))
        ) || 1;
        out[n] = Math.max(0, (out[n] ?? 0) - used);
      }
    }
  }
  return out;
}

function nightInRange(dateISO, rng) {
  if (!rng) return false;
  // nights range is [start, end) per parsePgRange
  return rng.start <= dateISO && dateISO < rng.end;
}
 

function parsePgRange(txt) {
  if (!txt) return null;
  // Example: ["2025-08-01","2025-08-05")
  const m = String(txt).match(/(\d{4}-\d{2}-\d{2}).*?(\d{4}-\d{2}-\d{2})/);
  return m ? { start: m[1], end: m[2] } : null;
}

export function acQuoteLocal({
  roomTypeId,
  startDate,
  endDate,
  seasons = [],
  modifiers = [],
  remainingByNight = {},
}) {
  const nights = acNightsBetween(startDate, endDate);
  if (!nights.length) return { total: 0, nights: [] };

  const rows = nights.map(date => {
    const { hasPrice, price: base } = acFindSeasonPrice(roomTypeId, date, seasons);
    if (!hasPrice) return { date, base: 0, price: 0, applied: ['no_price'] };
    let p = Number(base || 0);
    const applied = [];

    // weekday modifiers
    const dow = ((new Date(date).getDay() + 6) % 7) + 1; // 1..7 Mon..Sun
    modifiers
      .filter(m => m.rule_type === 'weekday' && Number(m.day_of_week) === dow)
      .forEach(m => { p = applyMod(p, m); applied.push(describeMod(m)); });

    // low inventory
    modifiers
      .filter(m => m.rule_type === 'low_inventory' && m.inventory_threshold != null)
      .forEach(m => {
        const left = remainingByNight?.[date] ?? Infinity;
        if (left <= Number(m.inventory_threshold)) {
          p = applyMod(p, m); applied.push(describeMod(m, { left }));
        }
      });

    return { date, base, price: round2(p), applied };
  });

  let total = rows.reduce((s, r) => s + r.price, 0);

  // LOS (length-of-stay) – apply to total once
  const losMods = modifiers.filter(m => m.rule_type === 'los' && Number(m.min_nights || 0) <= nights.length);
  for (const m of losMods) {
    total = applyMod(total, m);
    rows.push({ date: 'LOS', base: null, price: null, applied: [describeMod(m, { nights: nights.length })] });
  }

  return { total: round2(total), nights: rows };
}

function applyMod(amount, m) {
  const t = String(m.modifier_type || '').toLowerCase();
  const v = Number(m.value || 0);
  if (t === 'percentage') return amount * (1 + v / 100);
  if (t === 'flat') return amount + v;
  return amount;
}
function describeMod(m, extra={}) {
  const t = m.rule_type;
  if (t === 'weekday') return `weekday ${m.day_of_week}: ${m.value}${m.modifier_type === 'percentage' ? '%' : ''}`;
  if (t === 'low_inventory') return `low inventory≤${m.inventory_threshold}: ${m.value}${m.modifier_type === 'percentage' ? '%' : ''}`;
  if (t === 'los') return `LOS≥${m.min_nights}: ${m.value}${m.modifier_type === 'percentage' ? '%' : ''}`;
  return `${t}: ${m.value}`;
}
function round2(n){ return Math.round((Number(n)||0) * 100) / 100; }

function nightsOverlap(a, b) {
  if (!a || !b) return false;
  return (a.start < b.end) && (a.end > b.start); // [start, end)
}
